import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useRetrieveWeekTimeRecords } from "@planzone/view-models";
import { formatLocalDate, toFixed } from "@pages/sharedTimeSheetUtils";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { AllocationUnit } from "@common/allocationUnit";
import { useTimeRecordsListingColumns } from "./columns";
import { Row } from "./row";
import { Props } from "./types";
import styles from "./styles.scss";

function WeekTimeRecords({ scope, week, validationContext }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const startDate = formatLocalDate(week[0]);
  const endDate = formatLocalDate(week[week.length - 1]);
  const {
    isRequestPending,
    isRequestFailure,
    isDirty,
    timeRecords,
    retrieveWeekTimeRecords,
  } = useRetrieveWeekTimeRecords(scope, startDate, endDate);
  const { columns, template, label } = useTimeRecordsListingColumns();
  const total = timeRecords.reduce((sum, { timeSpent }) => sum + timeSpent, 0);

  React.useEffect(() => {
    !isRequestPending && retrieveWeekTimeRecords({ scope, startDate, endDate });
  }, [JSON.stringify(scope), startDate, endDate]);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestFailure &&
      isDirty &&
      retrieveWeekTimeRecords({ scope, startDate, endDate });
  }, [isDirty, isRequestPending]);

  return (
    <div className={styles.weekTimeRecords}>
      <div className={styles.header}>
        <div className={styles.title}>
          <p>{t("weekTimeRecords.title")}</p>
          <span className={styles.count}>{timeRecords.length}</span>
        </div>
        <div className={styles.headerRight}>
          <p className={styles.total}>
            {toFixed(total)} <AllocationUnit />
          </p>
          <Safeguard useCases={[UseCases.editTimeRecordsListingConfiguration]}>
            {({ canEditTimeRecordsListingConfiguration }) =>
              canEditTimeRecordsListingConfiguration && (
                <div
                  className={styles.configuration}
                  title={t("timeRecordsListingConfiguration.title")}
                  onClick={() =>
                    pushView({
                      key: UseCases.retrieveTimeRecordsListingConfiguration,
                      data: undefined,
                    })
                  }
                >
                  <CogWheelIcon className={styles.cogIcon} />
                </div>
              )
            }
          </Safeguard>
        </div>
      </div>
      {timeRecords.length > 0 ? (
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(({ key }) => (
                <th key={key}>{label(key)}</th>
              ))}
              <th className={styles.actions} />
            </tr>
          </thead>
          <tbody>
            {timeRecords.map((timeRecord) => (
              <Row
                key={timeRecord.uuid}
                timeRecord={timeRecord}
                columns={columns}
                template={template}
                validationContext={validationContext}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <p className={styles.empty}>
          {isRequestPending ? "…" : t("weekTimeRecords.empty")}
        </p>
      )}
    </div>
  );
}

export { WeekTimeRecords };
